// ============================================
// Language Context
// Simple i18n system with EN/ES support.
// PDF/form names (Consent, Care Plan, etc.) are intentionally kept in English
// and therefore NOT translated here.
// ============================================

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type Language = 'en' | 'es';

type TranslationDict = Record<string, string>;

const STORAGE_KEY = 'css_forms_language';

// English translations (source of truth).
// Keys are grouped with dot-notation prefixes by page/section.
const en: TranslationDict = {
  // Global nav
  'nav.newForm': 'New Form',
  'nav.language': 'Language',

  // Forms Submission header (FormWizard.tsx)
  'wizard.title': 'Forms Submission',
  'wizard.subtitle': 'Complete all sections to generate your PDF documents',
  'wizard.autoSaveNotice': 'Your progress is automatically saved every 30 seconds',
  'wizard.stepHeading': 'Step {current}: {title}',
  'wizard.stepOf': 'Step {current} of {total}',

  // Progress bar
  'progress.label': 'Progress',
  'progress.complete': '{percent}% Complete',

  // Generic step titles (PDF/form names intentionally stay in English)
  'step.documentSelection.title': 'Document Selection',
  'step.documentSelection.description': 'Choose group and individual documents',
  'step.memberInfo.title': 'Member Info',
  'step.memberInfo.description': 'Basic member details',
  'step.signature.title': 'Signature',
  'step.signature.description': 'Digital signature',

  // Navigation buttons
  'button.previous': 'Previous',
  'button.next': 'Next',
  'button.saveDraft': 'Save as Draft',
  'button.saving': 'Saving...',
  'button.generatePdfs': 'Generate Selected PDFs ({count})',
  'button.generatePdfsShort': 'Generate PDFs',
  'button.generating': 'Generating...',
  'button.dismiss': 'Dismiss',
  'progress.generatingPdfs': 'Generating PDFs (this may take a moment)...',
  'button.goToField': 'Go to field',

  // Error/validation banners
  'errors.fixFollowing': 'Please fix the following errors:',
  'errors.completeFollowing': 'Please complete the following:',
  'errors.form': 'Form',

  // Draft / restore
  'draft.restorePrevious': 'Restore Previous Progress?',
  'draft.restoreMessage': 'We found a saved form in progress. Would you like to continue where you left off?',
  'draft.restoreButton': 'Restore',
  'draft.startFresh': 'Start Fresh',
  'draft.saveFirst': 'Fill all required Member Info fields first',
  'draft.saveTooltip': 'Save as Draft',

  // Document Selection section
  'docSelection.groupHeading': 'Select Document Group',
  'docSelection.groupSubtitle': 'Choose a pre-defined group flow. You can also add extra individual documents below.',
  'docSelection.noGroup': 'No group selected',
  'docSelection.groupIncludes': 'Group includes:',
  'docSelection.individualHeading': 'Select Individual Documents',
  'docSelection.individualSubtitle': 'Group documents are hidden here to prevent duplicates.',
  'docSelection.finalSelected': 'Final selected documents ({count})',
  'docSelection.noneSelected': 'No documents selected yet.',

  // Group options (groups are admin-facing labels — keeping short names; only the prefix is localized)
  'docSelection.group.contra_costa_adult': 'Group 1: Contra Costa Health Plan',
  'docSelection.group.contra_costa_children': 'Group 2: Contra Costa Health Plan (Children)',
  'docSelection.group.partnership_adult': 'Group 3: Partnership Healthplan',
  'docSelection.group.partnership_children': 'Group 4: Partnership Healthplan (Children)',

  // Member Info section
  'memberInfo.readOnlyNotice': 'Member information is pre-filled and cannot be edited. Please proceed to the Signature step.',
  'memberInfo.pdfNotice': 'This information will be used across all 5 PDF documents. Please ensure accuracy.',
  'memberInfo.firstName': 'First Name',
  'memberInfo.firstNamePlaceholder': 'Enter first name',
  'memberInfo.firstNameRequired': 'First name is required',
  'memberInfo.lastName': 'Last Name',
  'memberInfo.lastNamePlaceholder': 'Enter last name',
  'memberInfo.lastNameRequired': 'Last name is required',
  'memberInfo.dateOfBirth': 'Date of Birth',
  'memberInfo.dateOfBirthRequired': 'Date of birth is required',
  'memberInfo.medicalId': 'Medi-Cal ID (CIN)',
  'memberInfo.medicalIdPlaceholder': 'Enter Medi-Cal ID',
  'memberInfo.medicalIdRequired': 'Medi-Cal ID is required',
  'memberInfo.medicalIdInvalid': 'Please enter a valid Medi-Cal ID',
  'memberInfo.medicalIdExists': 'Medical ID already exists in the system',
  'memberInfo.medicalIdExistsWarning': 'Warning: This Medical ID already exists in the system. Please use a different ID or contact support.',
  'memberInfo.phone': 'Phone Number',
  'memberInfo.phoneInvalid': 'Phone number must be exactly 10 digits',
  'memberInfo.email': 'Email Address',
  'memberInfo.emailPlaceholder': 'email@example.com',
  'memberInfo.emailInvalid': 'Please enter a valid email address',
  'memberInfo.addressHeading': 'Address',
  'memberInfo.street': 'Street Address',
  'memberInfo.streetPlaceholder': '123 Main Street, Apt 4',
  'memberInfo.streetRequired': 'Street address is required',
  'memberInfo.city': 'City',
  'memberInfo.cityPlaceholder': 'Los Angeles',
  'memberInfo.cityRequired': 'City is required',
  'memberInfo.state': 'State',
  'memberInfo.stateRequired': 'State is required',
  'memberInfo.zipCode': 'ZIP Code',
  'memberInfo.zipCodeRequired': 'ZIP code is required',
  'memberInfo.zipCodeInvalid': 'ZIP code must be 12345 or 12345-6789',

  // Language picker
  'languagePicker.welcome': 'Welcome to California Support Services',
  'languagePicker.chooseLanguage': 'Please choose your preferred language to continue:',
  'languagePicker.english': 'English',
  'languagePicker.spanish': 'Español (Spanish)',
  'languagePicker.continue': 'Continue',

  // Consent section
  'consent.autoFillNotice': 'Member information (First Name, Last Name, Address, Phone, Email, ID) will be auto-filled from Step 1.',
  'consent.minorIndicator': 'Please indicate if the Member is under the age of 18.',
  'consent.minorNote': 'Note: May require additional forms to accompany intake documentation.',
  'consent.noticeHeading': 'Notice to Medi-Cal beneficiary:',
  'consent.authorizationIntro': 'By signing this form, I, the individual named herein, authorize California Support Services to use and disclose my protected health information (PHI) and personally identifiable information (PII) for the purposes of CalAIM initiatives, programs, and services, including but not limited to:',
  'consent.ecmService': 'Enhanced Care Management (ECM)',
  'consent.csServices': 'Community Supports (CS) Housing Transition Navigation Services / Deposit Services / Tenancy and Sustaining Services',
  'consent.understandHeading': 'I understand that:',
  'consent.understand1': 'Signing this Authorization to release health information is voluntary. I may choose not to sign, which could affect my ability to participate in the listed programs but will not impact my access to treatment, services, or other benefits.',
  'consent.understand2': 'California Support Services and affiliated individuals or entities are required by California law to keep my health information confidential.',
  'consent.understand3': 'Information shared under this Authorization may, under certain conditions, be re-disclosed and may no longer be protected by State and Federal confidentiality laws, but only within the scope of the program\'s purpose or referrals.',
  'consent.understand4': 'I have the right to review or obtain a copy of the health information being shared.',
  'consent.understand5': 'I may request a copy of this Authorization at any time, and one will be provided to me.',
  'consent.withdrawalInfo': 'This Authorization may be withdrawn or revoked at any time by submitting a written or emailed request to the administration or office of California Support Services. The revocation will take effect upon receipt and processing by California Support Services. However, any actions taken or disclosures made in reliance on this Authorization prior to the receipt of the revocation will remain valid. I acknowledge that, upon revocation of this Authorization, California Support Services reserves the right to discontinue or decline services as permitted by applicable laws and regulations.',
  'consent.accuracyStatement': 'By signing this document, I confirm that all information, including any supporting documents, provided for the purpose of eligibility assessment and service authorization is accurate and truthful to the best of my knowledge and belief.',
  'consent.signatureNote': 'Note: Signature and Date will be collected in the Signature section (Step 7).',
  'consent.authorizedRepHeading': 'Authorized Representative (if applicable)',
  'consent.signingOnBehalf': 'This form is being signed on behalf of the Medi-Cal Beneficiary',
  'consent.signingOnBehalfDesc': 'Complete the corresponding forms. This information will be considered and applicable within all other documentation going forward.',
  'consent.printedName': 'Printed Name',
  'consent.printedNamePlaceholder': 'Full legal name',
  'consent.relationshipToMember': 'Relationship to Member',
  'consent.relationshipPlaceholder': 'e.g., Parent, Guardian, Conservator',
  'consent.printedNameRequired': 'Printed name is required',
  'consent.relationshipRequired': 'Relationship is required',

  // Validation messages
  'validation.printedNameRequiredSigning': 'Printed name is required when signing',
  'validation.digitalSignatureRequired': 'Digital signature is required when signing',
  'validation.signatureDateRequired': 'Signature date is required when signing',
  'validation.printedNameRequiredOnBehalf': 'Printed name is required when signing on behalf',
  'validation.relationshipRequired': 'Relationship to member is required',
  'validation.currentHousingSituationRequired': 'Current housing situation is required',
  'validation.assessorNameRequired': 'Assessor name is required',
  'validation.assessmentDateRequired': 'Assessment date is required',
  'validation.leadCareManagerRequired': 'Lead Care Manager name is required',
  'validation.carePlanProblemRequired': 'At least one problem must be defined in the care plan',
  'validation.consentDeclineRequired': 'Please initial either CONSENT or DECLINE',
  'validation.expirationOptionRequired': 'Please initial either Standard or Custom expiration option',
  'validation.customExpirationDateRequired': 'Custom expiration date is required when choosing custom expiration',
  'validation.selectDocumentRequired': 'Select at least one document group or individual document',
  'validation.phoneNumberFormat': 'Phone number must be exactly 10 digits (numbers only)',
  'validation.emailFormat': 'Please enter a valid email address',
  'validation.zipCodeRequired': 'ZIP code is required',
  'validation.zipCodeFormat': 'ZIP code must be in format 12345 or 12345-6789',
  'validation.selectInfoToRelease': 'Select at least one type of information to release',
  'validation.providerFromRequired': 'Provider (From) name is required',
  'validation.providerToRequired': 'Provider (To) name is required',

  // ROI (Release of Information) section
  'roi.autoFillNotice': 'Authorization for Release of Information (ROI) - Member information will be auto-filled from Step 1.',
  'roi.authorizingOrgHeading': 'Authorizing Organization',
  'roi.ecmProviderName': 'ECM Provider Name',
  'roi.ecmProviderNamePlaceholder': 'California Support Services, LLC',
  'roi.infoToReleaseHeading': 'Information to be Released',
  'roi.checkAllApply': 'Check all that apply',
  'roi.carePlans': 'Care plans',
  'roi.medicalHistory': 'Medical history & diagnoses',
  'roi.mentalHealth': 'Mental/behavioral health information',
  'roi.substanceUse': 'Substance use treatment information',
  'roi.medications': 'Medications',
  'roi.hospitalizationER': 'Hospitalization / ER records',
  'roi.socialServices': 'Social services & community supports',
  'roi.billing': 'Billing & eligibility information',
  'roi.other': 'Other',
  'roi.specifyOtherInfo': 'Please specify other information',
  'roi.specifyOtherInfoPlaceholder': 'Describe other information to release',
  'roi.fromHeading': 'From (Name of Provider/Agency)',
  'roi.toHeading': 'To (Name of Provider/Agency)',
  'roi.providerOrgName': 'Provider/Organization Name',
  'roi.providerOrgPlaceholder': 'Organization name',
  'roi.providerNameRequired': 'Provider name is required',
  'roi.purposeHeading': 'Purpose of Disclosure',
  'roi.careCoordination': 'Coordination of care',
  'roi.ecmServiceDelivery': 'ECM service delivery',
  'roi.carePlanning': 'Care planning & case management',
  'roi.continuityOfCare': 'Continuity of care',
  'roi.eligibility': 'Eligibility determination',
  'roi.specifyOtherPurpose': 'Please specify other purpose',
  'roi.specifyOtherPurposePlaceholder': 'Describe other purpose',
  'roi.expirationHeading': 'Expiration of Authorization',
  'roi.selectOne': 'Select one',
  'roi.expOneYear': 'One (1) year from the date signed',
  'roi.expTermination': 'Upon termination from ECM services',
  'roi.expSpecificDate': 'Specific date',
  'roi.expirationDate': 'Expiration Date',
  'roi.expirationDateRequired': 'Expiration date is required',
  'roi.specialConsentHeading': 'Special Consent for Sensitive Information (Initial if applicable)',
  'roi.specialConsentNote': 'If releasing sensitive information, member must initial below:',
  'roi.mentalHealthInfo': 'Mental Health Information',
  'roi.sudInfo': 'Substance Use Disorder Information (42 CFR Part 2)',
  'roi.hivInfo': 'HIV/AIDS Information',
  'roi.initialPlaceholder': 'Initial',
  'roi.witnessHeading': 'Witness (Optional)',
  'roi.witnessNote': 'A witness may be required for certain authorizations.',
  'roi.witnessName': 'Witness Name',
  'roi.witnessNamePlaceholder': 'Full name of witness',
  'roi.witnessDate': 'Witness Date',
  'roi.witnessSignatureNote': 'Note: Witness signature will be collected in the Signature section.',

  // Housing Assessment section
  'housing.title': 'Community Supports Assessment Form',
  'housing.subtitle': 'Housing Navigation & Housing Sustaining (CalAIM)',
  'housing.referralSource': 'Referral Source',
  'housing.mcp': 'MCP',
  'housing.ecm': 'ECM',
  'housing.provider': 'Provider',
  'housing.self': 'Self',
  'housing.other': 'Other',
  'housing.specifyOther': 'Specify other',
  'housing.managedCarePlan': 'Managed Care Plan (MCP)',
  'housing.managedCarePlanPlaceholder': 'e.g., Partnership HealthPlan',
  'housing.assessmentDate': 'Assessment Date',
  'housing.required': 'Required',
  'housing.housingStatus': 'A. Housing Status (Required)',
  'housing.currentSituation': 'Current Housing Situation (check one):',
  'housing.selectSituation': 'Please select current housing situation',
  'housing.lengthSituation': 'Length of Current Situation',
  'housing.primaryBarriers': 'Primary Barriers to Housing',
  'housing.checkAllApply': '(check all that apply)',
  'housing.eligibilityMedical': 'B. Eligibility & Medical Appropriateness',
  'housing.healthNeeds': 'Medical, Behavioral Health, or Functional Needs that justify CS services',
  'housing.howImprove': 'How housing support will improve health outcomes and reduce costs',
  'housing.navigationAssessment': 'C. Housing Navigation Assessment',
  'housing.sustaingingAssessment': 'D. Housing Sustaining Assessment',
  'housing.riskSafety': 'E. Risk & Safety Screening',
  'housing.memberGoals': 'F. Member Goals',
  'housing.serviceRecommendation': 'G. Service Recommendation',
  'housing.assessorInfo': 'Assessor Information',
  'housing.assessorName': 'Assessor Name & Title',
  'housing.assessorNamePlaceholder': 'Enter assessor name and title',
  'housing.clearSignature': 'Clear Signature',
  'housing.drawSignature': 'Draw Signature',
  'housing.typeSignature': 'Type Signature',
  'housing.uploadSignature': 'Upload Signature',
  'housing.typeYourName': 'Type your name',
  'housing.chooseFile': 'Choose File',
  'housing.uploadImageFile': 'Please upload an image file',
  'housing.fileSizeLimit': 'File size must be less than 5MB',

  // Housing barriers
  'housing.lowIncome': 'Low/no income',
  'housing.poorCredit': 'Poor credit / rental debt',
  'housing.priorEviction': 'Prior eviction',
  'housing.criminalHistory': 'Criminal justice history',
  'housing.disability': 'Disability / chronic condition',
  'housing.behavioralHealth': 'Behavioral health needs',
  'housing.lackDocuments': 'Lack of documents (ID, SSN, birth certificate)',
  'housing.languageBarriers': 'Language barriers',
  'housing.transportation': 'Transportation barriers',
  'housing.lengthSituationLabel': 'Length of Current Situation',

  // Housing situations
  'housing.unsheltered': 'Unsheltered',
  'housing.unshelteredDesc': 'Street, encampment, vehicle',
  'housing.emergencyShelter': 'Emergency shelter / transitional housing',
  'housing.doubledUp': 'Temporarily doubled-up',
  'housing.imminentRisk': 'At imminent risk of homelessness', 
  'housing.imminentRiskDesc': 'Within 30-60 days',
  'housing.evictionRisk': 'Housed but at risk of eviction',
  'housing.recentlyHoused': 'Recently housed',
  'housing.recentlyHousedDesc': 'Within last 12 months',

  // Risk factors  
  'housing.creditHistory': 'Credit history',
  'housing.criminalBackground': 'Criminal background',
  'housing.poorRentHistory': 'Poor rental history',
  'housing.noIncome': 'No income',
  'housing.miscAppointments': 'Missed appointments/services',
  'housing.behavioralSymptoms': 'Behavioral health symptoms',
  'housing.lackTenancySkills': 'Lack of tenancy skills',

  // Member support needs
  'housing.supportNeedsLabel': 'Member Needs Support With (check all that apply):',
  'housing.tenantRights': 'Understanding tenant rights/responsibilities',
  'housing.budgeting': 'Budgeting and rent planning',
  'housing.utilitySetup': 'Utility setup/maintenance',
  'housing.landlordComm': 'Communication with landlord',
  'housing.crisisPrevention': 'Crisis prevention and early intervention',
  'housing.ongoingServices': 'Connecting to ongoing services/supports',

  // Risk & Safety screening
  'housing.safetyConcerns': 'Immediate safety concerns in current housing:',
  'housing.evictionRisk30': 'Risk of eviction within 30 days:',
  'housing.repeatedLoss': 'History of repeated housing loss (2+ times in 24 months):',
  'housing.mitigationPlan': 'If Yes to any, describe mitigation plan:',
  'housing.mitigationPlanPlaceholder': 'Describe the mitigation plan...',

  // Member goals
  'housing.memberGoalsLabel': 'Member-Stated Housing Goal(s)',
  'housing.memberGoalsPlaceholder': 'What are the member\'s housing goals?',

  // Service recommendation
  'housing.recommendedSupports': 'Recommended Community Support(s):',
  'housing.housingNavigation': 'Housing Navigation',
  'housing.housingSustaining': 'Housing Sustaining',
  'housing.both': 'Both',
  'housing.durationFrequency': 'Duration/Frequency Requested',
  'housing.durationFrequencyPlaceholder': 'e.g., Weekly for 6 months',
  'housing.rationale': 'Rationale',
  'housing.rationalePlaceholder': 'Explain the rationale for this recommendation...',

  // Assessor information (updated keys)
  'housing.assessorTitle': 'Title',
  'housing.titlePlaceholder': 'Job title',
  'housing.assessorSignatureDate': 'Assessor Signature Date',
  'housing.assessorSignature': 'Assessor Signature',
  'housing.assessorSigRequired': 'Assessor name is required',

  // Signature options
  'housing.draw': 'Draw',
  'housing.type': 'Type',
  'housing.upload': 'Upload',
  'housing.typeFullName': 'Type assessor\'s full name',
  'housing.signaturePreview': 'Signature preview',
  'housing.uploadedSignature': 'Uploaded signature',
  'housing.remove': 'Remove',
  'housing.signatureCaptured': 'Signature captured:',

  // Common labels
  'housing.yes': 'Yes',
  'housing.no': 'No',

  // Housing - Eligibility & Medical
  'housing.healthRelatedNeeds': 'Health-Related Need(s) Contributing to Housing Instability',
  'housing.healthRelatedNeedsPlaceholder': 'Describe health-related needs...',
  'housing.relevantDiagnoses': 'Relevant Diagnoses or Conditions (if applicable)',
  'housing.relevantDiagnosesPlaceholder': 'List relevant diagnoses...',
  'housing.howHousingHelps': 'How housing support will improve or stabilize health outcomes',
  'housing.howHousingHelpsPlaceholder': 'Describe how housing support will help...',
  'housing.lengthPlaceholder': 'e.g., 6 months, 2 years',
  'housing.describeOtherBarriers': 'Describe other barriers',

  // Housing Navigation
  'housing.navCompleteNote': '(Complete if requesting Housing Navigation)',
  'housing.navAssistanceLabel': 'Member Needs Assistance With (check all that apply):',
  'housing.navHousingSearch': 'Housing search and unit identification',
  'housing.navApplications': 'Completing housing applications',
  'housing.navDocuments': 'Securing identification/documents',
  'housing.navAccommodation': 'Reasonable accommodation requests',
  'housing.navLandlord': 'Landlord engagement/negotiation',
  'housing.navLeaseTerms': 'Understanding lease terms',
  'housing.navMoveIn': 'Move-in coordination',
  'housing.navRentalAssistance': 'Connection to rental assistance or deposits',

  // Housing Preferences
  'housing.preferencesHeading': 'Housing Preferences',
  'housing.locationCity': 'Location/City',
  'housing.locationCityPlaceholder': 'Preferred city or area',
  'housing.unitType': 'Unit Type:',
  'housing.unitStudio': 'Studio',
  'housing.unit1BR': '1BR',
  'housing.unit2BR': '2BR',
  'housing.unitOther': 'Other',
  'housing.accessibilityNeeds': 'Accessibility Needs:',
  'housing.accessibilityNone': 'None',
  'housing.accessibilityADA': 'ADA',
  'housing.accessibilityOther': 'Other',
  'housing.incomeSources': 'Income Source(s):',
  'housing.incomeSsiSsdi': 'SSI/SSDI',
  'housing.incomeEmployment': 'Employment',
  'housing.incomeGa': 'GA',
  'housing.incomeNone': 'None',
  'housing.incomeOther': 'Other',

  // Housing Sustaining
  'housing.sustainCompleteNote': '(Complete if requesting Housing Sustaining)',
  'housing.leaseStatus': 'Current Lease Status:',
  'housing.newLease': 'New lease',
  'housing.existingLease': 'Existing lease',
  'housing.informalArrangement': 'Informal arrangement',
  'housing.riskFactorsLabel': 'Risk Factors for Housing Loss (check all that apply):',
  'housing.riskRentUtilities': 'Difficulty paying rent/utilities',
  'housing.riskLeaseViolations': 'Lease violations or notices',
  'housing.riskConflict': 'Conflict with landlord/neighbors',
  'housing.riskMaintainingUnit': 'Difficulty maintaining unit',

  // Partnership Consent section
  'pc.title': 'Community Supports Consent Form',
  'pc.subtitle': 'Partnership HealthPlan of California - Release of Information',
  'pc.introParagraph': 'Health care providers, health payers, and social services agencies have joined together to provide Community Supports (CS) Services to help promote your health and well-being. To allow Partnership HealthPlan of California ("Partnership") and/or other entities to share your health care and other personal information with each other to help provide you with these CS Services, you must give your authorization first. By filling out this form, you are authorizing the use and release of your health care and other personal information by the following entities participating in CS ("CS Entities"): health care providers such as hospitals, physicians, and pharmacies; Partnership and other managed care plans that administer Medi-Cal benefits and pay for services you receive under Medi-Cal; community-based organizations that must comply with health care privacy laws; school-based providers such as nurses, social workers, and counselors; the California Departments of Health Care Services, Public Health, Social Services, and Developmental Services; and county agencies including, but not limited to, mental health plans; and providers and case managers at correctional facilities, but only for the purposes set forth below. Your authorization will permit CS Entities to use and release your health care and other personal information for the following purposes ("Purposes"): to allow these entities to address your health-related social needs, including housing transition navigation services; housing deposits; short-term post-hospitalization housing; short-term residential care including housing, meals, and ongoing medical monitoring; caregiver services; day habilitation programs; assistance transitioning from a nursing facility to an assisted living facility or private residence; in-home support services; home adaptations or modifications; medically tailored meals; and sobering centers ("Purposes"). The information that you authorize for use and release may be shared in a secure electronic format, in writing, or verbally to coordinate CS Services for you.',
  'pc.authStatement': 'I authorize and ask that Partnership HealthPlan of California and participating CS Entities named in Attachment A to use and share any of my health care or other personal information with each other for the reason stated in this Authorization.',
  'pc.chooseOne': 'Choose ONE of the following options:',
  'pc.initialHere': 'INITIAL HERE',
  'pc.consentCommHeading': 'Consent for communication by CS Program:',
  'pc.consentCommText': 'By putting my initials here, I am allowing ALL of the CS Entities listed in Attachment A to use and share my health care and other personal information about my medical history, physical and mental condition, and receipt of social services, and to communicate with each other in order to provide CS Services. The types of health and other confidential information that I am authorizing between CS Entities include:',
  'pc.consentComm1': 'Protected health information (PHI), including information regarding my health care, medical history, lab test results, and current or future conditions and treatment;',
  'pc.consentComm2': 'Mental health information, including current and past diagnoses and treatments of my mental health conditions, excluding psychotherapy notes which are only shared if I sign a separate consent form;',
  'pc.consentComm3': 'Individualized Education Program information and other information about social services provided in school;',
  'pc.consentComm4': 'Medi-Cal eligibility/enrollment information, which includes income and certain other demographic and geographic information pertaining to my eligibility for Services and benefits;',
  'pc.consentComm5': 'Housing/homelessness information, including my housing status, history, and supports; and',
  'pc.consentComm6': 'Limited criminal justice information, including booking data, dates and location of incarceration, and supervision status. My consent does not apply to my criminal history, charges, and immigration status.',
  'pc.declineHeading': 'Decline to participate in CS:',
  'pc.declineText': 'I understand that the CS program allows CS Entities to be in contact with each other to coordinate my care. I decline to participate in the CS program. I can ask to participate in case management programs for which I am eligible.',
  'pc.furtherAuthIntro': 'Further, by putting my initials below, I specifically authorize the release of the following information (this information will NOT be released unless you specifically allow it)',
  'pc.mentalHealthText': 'Mental health information, including diagnosis, treatment plan, and provider name. This does not include psychotherapy notes which are only shared if I sign a separate consent form.',
  'pc.hivText': 'HIV Test Results (Health & Safety Code § 120980 (g))',
  'pc.sudHeading': 'Substance Use Disorder Information',
  'pc.sudText': 'Substance use disorder ("SUD") records are protected by federal confidentiality rules (42 CFR Part 2). The federal rules do not allow any further release of information that finds a patient as having or having had a substance use disorder either by reference to publicly available information, or through proof of such identification by another person unless further release is permitted by the written consent of the person whose information is being given or as otherwise permitted by 42 CFR Part 2. The federal rules restrict any use of the information to investigate or prosecute, with regard to a crime, any patient with a substance use disorder, except as provided at §§ 2.12(c)(5) and 2.65. By filling out this section, you are authorizing CS Entities to use and release the following SUD information for the Purposes described in this form: your current and past drug or alcohol use diagnoses, medications, treatment, lab tests, trauma history, facility discharges, and any other SUD information about you that comes from a substance/alcohol use disorder provider subject to federal SUD confidentiality regulations (42 C.F.R. Part 2). SUD records (or information therein) that are used or disclosed for treatment, payment, or health care operations by certain CS Entities, including health care providers, may be redisclosed as permitted in the federal HIPAA regulations, except for uses and disclosures for civil, criminal, administrative, and legislative proceedings against you. Your SUD counseling notes will not be shared unless you sign a separate consent form.',
  'pc.sudInitialText': 'Initial here to allow the CS Entities in Attachment A to use and share your SUD information as described above, excluding SUD counseling notes.',
  'pc.expirationHeading': 'Expiration of Form',
  'pc.expirationChooseOne': 'Choose ONE of the following options:',
  'pc.expirationStandard': 'Standard expiration:',
  'pc.expirationStandardText': 'This form will expire 1 year from today\'s date; OR',
  'pc.expirationCustom': 'Expiration:',
  'pc.expirationCustomText': 'This form will expire on:',
  'pc.expirationCustomNote': '. This date may not be less than 6 months (to participate in CS services), but may be more than 1 year from today\'s date.',
  'pc.understandHeading': 'I understand that:',
  'pc.understand1': 'I can revoke this Authorization at any time by calling Partnership at (800) 863-4115 or by sending a signed revocation request to:',
  'pc.understand1Address': 'Partnership HealthPlan of California\nAttn: Enhanced Health Services\n4665 Business Center Drive\nFairfield, CA 94534',
  'pc.understand2': 'A revocation is effective when received, but may not apply to information already shared, based on my prior consent to use or release information.',
  'pc.understand3': 'I can choose not to sign this form and doing so will not affect my treatment or care, my eligibility for or ability to receive Services, or the payment for Services. However, my ability to participate in CS Services may be affected by not signing this Authorization.',
  'pc.understand4': 'Even if I do not sign this form, under federal and state privacy laws, some of the CS Entities may share my confidential information for treatment, payment, and other purposes, but providers subject to federal substance use confidentiality laws generally may not share my substance use disorder information without my consent (42 CFR Part 2).',
  'pc.understand5': 'The information I authorize for use or release may be re-shared by CS Entities, but only in compliance with this Authorization and applicable law.',
  'pc.understand6': 'I can get a copy of the health information that is being shared.',
  'pc.understand7': 'I have the right to ask for a copy of this form and one will be sent to me.',
  'pc.understand8': 'I may obtain a list of all CS Entities to which my information has been disclosed, including those entities identified in Attachment A, by contacting Partnership.',
  'pc.understand9': 'If I voluntarily include my phone number above, I consent to the receipt of texts or calls to communicate with me about my consent choices and how my health and other confidential information may be shared (standard message and data rates may apply).',
  'pc.understand10': 'Each of the above rights extend to any representative I authorize under applicable law.',
  'pc.signatureNote': '[signature on next page]',
  'pc.signatureNoteDetail': 'Please proceed to Step 7 (Signature) to provide your digital signature. The signature will be applied to this form.',
  'pc.signatureMemberHeading': 'Signature of Member',
  'pc.signatureInstruction': 'If you are signing this Authorization on your own behalf, fill out the first line. If you are signing on behalf of someone else, fill out the second line. If you are signing on behalf of a minor aged 12-17, the minor should fill out the first line and you should fill out the second line.',
  'pc.signatureCheckNote1': 'Beneficiary\'s Name and Signature will be captured in Step 7 (Signature Section)',
  'pc.signatureCheckNote2': 'If signing as a Representative, please provide your information below:',
  'pc.repName': 'Representative\'s Name (if applicable)',
  'pc.repNamePlaceholder': 'Leave blank if you are the beneficiary',

  // Signature section
  'signature.digitalSignature': 'Digital Signature',
  'signature.memberOnly': 'Signature is for the member or authorized representative only. If you are staff preparing this form, leave this section blank.',
  'signature.staffInstruction': 'Staff instruction: do not sign here while filling the form. Leave signature fields empty for the actual signer.',
  'signature.signedBy': 'Signed By (Print Name)',
  'signature.signedByPlaceholder': 'Full legal name',
  'signature.date': 'Date',
  'signature.relationshipLabel': 'Relationship to Member (if not the member)',
  'signature.relationshipPlaceholder': 'e.g., Parent, Legal Guardian, Power of Attorney (leave blank if you are the member)',
  'signature.relationshipNote': 'Only fill this if you are signing on behalf of the member (authorized representative)',
  'signature.chooseMethod': 'Choose Signature Method',
  'signature.drawSignature': 'Draw Signature',
  'signature.typeSignature': 'Type Signature',
  'signature.uploadSignature': 'Upload Signature',
  'signature.clearSignature': 'Clear Signature',
  'signature.typeYourName': 'Type your name',
  'signature.clickToSign': 'Click above to sign',
  'signature.signaturePreview': 'Signature preview:',
  'signature.chooseFile': 'Choose File',
  'signature.dragDropFile': 'or drag and drop signature image',
  'signature.uploadImageFile': 'Please upload an image file',
  'signature.fileSizeLimit': 'File size must be less than 5MB',
  'signature.witnessHeading': 'Witness Signature (if applicable)',
  'signature.witnessNote': 'Complete this section only if a witness is required for authorization.',
  'signature.witnessChooseMethod': 'Witness Signature Method',
  'signature.witnessDrawSignature': 'Draw Witness Signature',
  'signature.witnessTypeSignature': 'Type Witness Signature',
  'signature.witnessUploadSignature': 'Upload Witness Signature',
  'signature.witnessClearSignature': 'Clear Witness Signature',
  'signature.witnessTypeYourName': 'Type witness name',
  'signature.witnessSignaturePreview': 'Witness signature preview:',
  'signature.witnessChooseFile': 'Choose Witness File',
  'signature.clearAndResign': 'Clear & Re-sign',
  'signature.signatureRequired': '⚠️ Signature Required - Please provide your signature above to continue',
  'signature.signatureCaptured': '✓ Signature captured successfully',
  'signature.staffInstructionBlank': 'If you are staff preparing this form, leave the signature section blank.',
  'signature.signatureAppearance': 'This is how your signature will appear on the documents.',
  'signature.legalAcknowledgment': 'Legal Acknowledgment',
  'signature.acknowledgmentText': 'By signing above, I acknowledge that:',
  'signature.acknowledgment1': 'I have read and understand all the information provided in this form',
  'signature.acknowledgment2': 'The information I have provided is true and accurate to the best of my knowledge',
  'signature.acknowledgment3': 'I am signing voluntarily and have not been coerced',
  'signature.acknowledgment4': 'I understand that my electronic signature has the same legal effect as a handwritten signature',
  'signature.acknowledgment5': 'I am the member named in this form, or I am authorized to sign on their behalf',
  'signature.witnessSignatureTitle': 'Witness Signature',
  'signature.witnessSpecified': 'A witness was specified',
  'signature.witnessSignBelow': 'Please have the witness sign below.',
  'signature.witnessSignatureArea': 'Witness signature',
  'signature.imageFileFormat': 'PNG, JPG up to 5MB',
  'signature.witnessSignatureAlt': 'Witness signature',
  'signature.signatureWillAppearOn': 'Your signature will appear on:',
  'signature.ecmReleaseOfInformation': 'ECM Release of Information',
  'signature.housingAssessment': 'Housing Assessment',
  'signature.cssConsentForm': 'CSS Consent Form',
  'signature.carePlan': 'Care Plan (16 pages)',
  'signature.consentPartnership': 'Consent Partnership',
  'signature.cyecmAssessment': 'CYECM Assessment',
  'signature.witnessSignatureCaptured': '✓ Witness signature captured successfully',

  // PDF Preview section
  'preview.title': 'Preview Generated PDFs',
  'preview.reviewDocuments': 'Review all {count} documents before submitting',
  'preview.generatedDocuments': 'Generated Documents',
  'preview.ready': 'Ready',
  'preview.pages': '{count} page{plural}',
  'preview.openNewTab': 'Open in New Tab',
  'preview.open': 'Open',
  'preview.download': 'Download',
  'preview.downloading': 'Downloading...',
  'preview.selectPdf': 'Select a PDF to preview',
  'preview.pdfsGenerated': '{count} PDFs generated and ready to submit',
  'preview.goBackEdit': 'Go Back & Edit',
  'preview.edit': 'Edit',
  'preview.submitSaveAll': 'Submit & Save All PDFs',
  'preview.submit': 'Submit',
  'preview.saving': 'Saving...',
  'preview.savingDatabase': 'Saving to Database...',
  'preview.back': 'Back',
  'preview.failedSave': 'Failed to save forms. Please try again.',
};

// Spanish translations
const es: TranslationDict = {
  // Global nav
  'nav.newForm': 'Nuevo Formulario',
  'nav.language': 'Idioma',

  // Forms Submission header
  'wizard.title': 'Envío de Formularios',
  'wizard.subtitle': 'Complete todas las secciones para generar sus documentos PDF',
  'wizard.autoSaveNotice': 'Su progreso se guarda automáticamente cada 30 segundos',
  'wizard.stepHeading': 'Paso {current}: {title}',
  'wizard.stepOf': 'Paso {current} de {total}',

  // Progress bar
  'progress.label': 'Progreso',
  'progress.complete': '{percent}% Completo',

  // Generic step titles (PDF/form names stay in English intentionally)
  'step.documentSelection.title': 'Selección de Documentos',
  'step.documentSelection.description': 'Elija grupo y documentos individuales',
  'step.memberInfo.title': 'Información del Miembro',
  'step.memberInfo.description': 'Datos básicos del miembro',
  'step.signature.title': 'Firma',
  'step.signature.description': 'Firma digital',

  // Navigation buttons
  'button.previous': 'Anterior',
  'button.next': 'Siguiente',
  'button.saveDraft': 'Guardar Borrador',
  'button.saving': 'Guardando...',
  'button.generatePdfs': 'Generar PDFs Seleccionados ({count})',
  'button.generatePdfsShort': 'Generar PDFs',
  'button.generating': 'Generando...',
  'button.dismiss': 'Descartar',
  'progress.generatingPdfs': 'Generando PDFs (esto puede tomar un momento)...',
  'button.goToField': 'Ir al campo',

  // Error/validation banners
  'errors.fixFollowing': 'Por favor corrija los siguientes errores:',
  'errors.completeFollowing': 'Por favor complete lo siguiente:',
  'errors.form': 'Formulario',

  // Draft / restore
  'draft.restorePrevious': '¿Restaurar progreso anterior?',
  'draft.restoreMessage': 'Encontramos un formulario guardado en progreso. ¿Desea continuar donde lo dejó?',
  'draft.restoreButton': 'Restaurar',
  'draft.startFresh': 'Empezar de Nuevo',
  'draft.saveFirst': 'Primero complete todos los campos obligatorios de Información del Miembro',
  'draft.saveTooltip': 'Guardar Borrador',

  // Document Selection section
  'docSelection.groupHeading': 'Seleccionar Grupo de Documentos',
  'docSelection.groupSubtitle': 'Elija un flujo de grupo predefinido. También puede agregar documentos individuales adicionales a continuación.',
  'docSelection.noGroup': 'Ningún grupo seleccionado',
  'docSelection.groupIncludes': 'El grupo incluye:',
  'docSelection.individualHeading': 'Seleccionar Documentos Individuales',
  'docSelection.individualSubtitle': 'Los documentos del grupo están ocultos aquí para evitar duplicados.',
  'docSelection.finalSelected': 'Documentos finales seleccionados ({count})',
  'docSelection.noneSelected': 'Ningún documento seleccionado aún.',

  // Group options
  'docSelection.group.contra_costa_adult': 'Grupo 1: Contra Costa Health Plan',
  'docSelection.group.contra_costa_children': 'Grupo 2: Contra Costa Health Plan (Niños)',
  'docSelection.group.partnership_adult': 'Grupo 3: Partnership Healthplan',
  'docSelection.group.partnership_children': 'Grupo 4: Partnership Healthplan (Niños)',

  // Member Info section
  'memberInfo.readOnlyNotice': 'La información del miembro está pre-completada y no se puede editar. Por favor proceda al paso de Firma.',
  'memberInfo.pdfNotice': 'Esta información se utilizará en los 5 documentos PDF. Por favor asegúrese de que sea precisa.',
  'memberInfo.firstName': 'Nombre',
  'memberInfo.firstNamePlaceholder': 'Ingrese nombre',
  'memberInfo.firstNameRequired': 'El nombre es obligatorio',
  'memberInfo.lastName': 'Apellido',
  'memberInfo.lastNamePlaceholder': 'Ingrese apellido',
  'memberInfo.lastNameRequired': 'El apellido es obligatorio',
  'memberInfo.dateOfBirth': 'Fecha de Nacimiento',
  'memberInfo.dateOfBirthRequired': 'La fecha de nacimiento es obligatoria',
  'memberInfo.medicalId': 'ID de Medi-Cal (CIN)',
  'memberInfo.medicalIdPlaceholder': 'Ingrese ID de Medi-Cal',
  'memberInfo.medicalIdRequired': 'El ID de Medi-Cal es obligatorio',
  'memberInfo.medicalIdInvalid': 'Por favor ingrese un ID de Medi-Cal válido',
  'memberInfo.medicalIdExists': 'El ID médico ya existe en el sistema',
  'memberInfo.medicalIdExistsWarning': 'Advertencia: Este ID médico ya existe en el sistema. Por favor use un ID diferente o contacte con soporte.',
  'memberInfo.phone': 'Número de Teléfono',
  'memberInfo.phoneInvalid': 'El número de teléfono debe tener exactamente 10 dígitos',
  'memberInfo.email': 'Correo Electrónico',
  'memberInfo.emailPlaceholder': 'correo@ejemplo.com',
  'memberInfo.emailInvalid': 'Por favor ingrese un correo electrónico válido',
  'memberInfo.addressHeading': 'Dirección',
  'memberInfo.street': 'Dirección de la Calle',
  'memberInfo.streetPlaceholder': '123 Calle Principal, Apt 4',
  'memberInfo.streetRequired': 'La dirección de la calle es obligatoria',
  'memberInfo.city': 'Ciudad',
  'memberInfo.cityPlaceholder': 'Los Ángeles',
  'memberInfo.cityRequired': 'La ciudad es obligatoria',
  'memberInfo.state': 'Estado',
  'memberInfo.stateRequired': 'El estado es obligatorio',
  'memberInfo.zipCode': 'Código Postal',
  'memberInfo.zipCodeRequired': 'El código postal es obligatorio',
  'memberInfo.zipCodeInvalid': 'El código postal debe ser 12345 o 12345-6789',

  // Language picker
  'languagePicker.welcome': 'Bienvenido a California Support Services',
  'languagePicker.chooseLanguage': 'Por favor elija su idioma preferido para continuar:',
  'languagePicker.english': 'English (Inglés)',
  'languagePicker.spanish': 'Español',
  'languagePicker.continue': 'Continuar',

  // Consent section
  'consent.autoFillNotice': 'La información del miembro (Nombre, Apellido, Dirección, Teléfono, Correo, ID) se completará automáticamente desde el Paso 1.',
  'consent.minorIndicator': 'Por favor indique si el Miembro es menor de 18 años.',
  'consent.minorNote': 'Nota: Puede requerir formularios adicionales para acompañar la documentación de admisión.',
  'consent.noticeHeading': 'Aviso al beneficiario de Medi-Cal:',
  'consent.authorizationIntro': 'Al firmar este formulario, yo, la persona nombrada en este documento, autorizo a California Support Services a usar y divulgar mi información de salud protegida (PHI) e información de identificación personal (PII) para los propósitos de las iniciativas, programas y servicios de CalAIM, incluyendo pero no limitado a:',
  'consent.ecmService': 'Gestión de Cuidado Mejorado (ECM)',
  'consent.csServices': 'Servicios de Apoyo Comunitario (CS) Servicios de Navegación de Transición de Vivienda / Servicios de Depósito / Servicios de Arrendamiento y Mantenimiento',
  'consent.understandHeading': 'Entiendo que:',
  'consent.understand1': 'Firmar esta Autorización para liberar información de salud es voluntario. Puedo elegir no firmar, lo cual podría afectar mi capacidad de participar en los programas listados pero no impactará mi acceso a tratamiento, servicios u otros beneficios.',
  'consent.understand2': 'California Support Services y las personas o entidades afiliadas están obligadas por la ley de California a mantener mi información de salud confidencial.',
  'consent.understand3': 'La información compartida bajo esta Autorización puede, bajo ciertas condiciones, ser re-divulgada y puede ya no estar protegida por las leyes de confidencialidad Estatales y Federales, pero solo dentro del alcance del propósito del programa o referencias.',
  'consent.understand4': 'Tengo el derecho de revisar u obtener una copia de la información de salud que se está compartiendo.',
  'consent.understand5': 'Puedo solicitar una copia de esta Autorización en cualquier momento, y se me proporcionará una.',
  'consent.withdrawalInfo': 'Esta Autorización puede ser retirada o revocada en cualquier momento enviando una solicitud escrita o por correo electrónico a la administración u oficina de California Support Services. La revocación tomará efecto al recibirse y procesarse por California Support Services. Sin embargo, cualquier acción tomada o divulgaciones hechas en base a esta Autorización antes del recibo de la revocación permanecerán válidas. Reconozco que, al revocar esta Autorización, California Support Services se reserva el derecho de descontinuar o declinar servicios según lo permitido por las leyes y regulaciones aplicables.',
  'consent.accuracyStatement': 'Al firmar este documento, confirmo que toda la información, incluyendo cualquier documento de apoyo, proporcionada para el propósito de evaluación de elegibilidad y autorización de servicios es precisa y veraz según mi mejor conocimiento y creencia.',
  'consent.signatureNote': 'Nota: La Firma y Fecha se recogerán en la sección de Firma (Paso 7).',
  'consent.authorizedRepHeading': 'Representante Autorizado (si aplica)',
  'consent.signingOnBehalf': 'Este formulario está siendo firmado en nombre del Beneficiario de Medi-Cal',
  'consent.signingOnBehalfDesc': 'Complete los formularios correspondientes. Esta información se considerará y aplicará dentro de toda otra documentación en adelante.',
  'consent.printedName': 'Nombre Impreso',
  'consent.printedNamePlaceholder': 'Nombre legal completo',
  'consent.relationshipToMember': 'Relación con el Miembro',
  'consent.relationshipPlaceholder': 'ej., Padre, Tutor, Conservador',
  'consent.printedNameRequired': 'El nombre impreso es obligatorio',
  'consent.relationshipRequired': 'La relación es obligatoria',

  // Validation messages  
  'validation.printedNameRequiredSigning': 'Se requiere nombre impreso al firmar',
  'validation.digitalSignatureRequired': 'Se requiere firma digital al firmar',
  'validation.signatureDateRequired': 'Se requiere fecha de firma al firmar',
  'validation.printedNameRequiredOnBehalf': 'Se requiere nombre impreso al firmar en nombre de otro',
  'validation.relationshipRequired': 'Se requiere la relación con el miembro',
  'validation.currentHousingSituationRequired': 'Se requiere la situación de vivienda actual',
  'validation.assessorNameRequired': 'Se requiere el nombre del evaluador',
  'validation.assessmentDateRequired': 'Se requiere la fecha de evaluación',
  'validation.leadCareManagerRequired': 'Se requiere el nombre del Gerente Principal de Atención',
  'validation.carePlanProblemRequired': 'Se debe definir al menos un problema en el plan de atención',
  'validation.consentDeclineRequired': 'Por favor inicial CONSENTIMIENTO o RECHAZAR',
  'validation.expirationOptionRequired': 'Por favor inicial opción Estándar o Personalizada de expiración',
  'validation.customExpirationDateRequired': 'Se requiere fecha de expiración personalizada al elegir expiración personalizada',
  'validation.selectDocumentRequired': 'Seleccione al menos un grupo de documentos o documento individual',
  'validation.phoneNumberFormat': 'El número de teléfono debe tener exactamente 10 dígitos (solo números)',
  'validation.emailFormat': 'Por favor ingrese una dirección de correo electrónico válida',
  'validation.zipCodeRequired': 'Se requiere código postal',
  'validation.zipCodeFormat': 'El código postal debe estar en formato 12345 o 12345-6789',
  'validation.selectInfoToRelease': 'Seleccione al menos un tipo de información para divulgar',
  'validation.providerFromRequired': 'Se requiere el nombre del proveedor (De)',
  'validation.providerToRequired': 'Se requiere el nombre del proveedor (Para)',

  // ROI (Release of Information) section
  'roi.autoFillNotice': 'Autorización para la Divulgación de Información (ROI) - La información del miembro se completará automáticamente desde el Paso 1.',
  'roi.authorizingOrgHeading': 'Organización Autorizadora',
  'roi.ecmProviderName': 'Nombre del Proveedor de ECM',
  'roi.ecmProviderNamePlaceholder': 'California Support Services, LLC',
  'roi.infoToReleaseHeading': 'Información a Divulgar',
  'roi.checkAllApply': 'Marque todas las que apliquen',
  'roi.carePlans': 'Planes de atención',
  'roi.medicalHistory': 'Historial médico y diagnósticos',
  'roi.mentalHealth': 'Información de salud mental/conductual',
  'roi.substanceUse': 'Información de tratamiento por uso de sustancias',
  'roi.medications': 'Medicamentos',
  'roi.hospitalizationER': 'Registros de hospitalización / sala de emergencias',
  'roi.socialServices': 'Servicios sociales y apoyos comunitarios',
  'roi.billing': 'Información de facturación y elegibilidad',
  'roi.other': 'Otro',
  'roi.specifyOtherInfo': 'Por favor especifique otra información',
  'roi.specifyOtherInfoPlaceholder': 'Describa otra información a divulgar',
  'roi.fromHeading': 'De (Nombre del Proveedor/Agencia)',
  'roi.toHeading': 'A (Nombre del Proveedor/Agencia)',
  'roi.providerOrgName': 'Nombre del Proveedor/Organización',
  'roi.providerOrgPlaceholder': 'Nombre de la organización',
  'roi.providerNameRequired': 'El nombre del proveedor es obligatorio',
  'roi.purposeHeading': 'Propósito de la Divulgación',
  'roi.careCoordination': 'Coordinación de la atención',
  'roi.ecmServiceDelivery': 'Prestación de servicios ECM',
  'roi.carePlanning': 'Planificación de atención y gestión de casos',
  'roi.continuityOfCare': 'Continuidad de la atención',
  'roi.eligibility': 'Determinación de elegibilidad',
  'roi.specifyOtherPurpose': 'Por favor especifique otro propósito',
  'roi.specifyOtherPurposePlaceholder': 'Describa otro propósito',
  'roi.expirationHeading': 'Expiración de la Autorización',
  'roi.selectOne': 'Seleccione una',
  'roi.expOneYear': 'Un (1) año a partir de la fecha de la firma',
  'roi.expTermination': 'Al terminar los servicios de ECM',
  'roi.expSpecificDate': 'Fecha específica',
  'roi.expirationDate': 'Fecha de Expiración',
  'roi.expirationDateRequired': 'La fecha de expiración es obligatoria',
  'roi.specialConsentHeading': 'Consentimiento Especial para Información Sensible (Iniciales si aplica)',
  'roi.specialConsentNote': 'Si se divulga información sensible, el miembro debe poner sus iniciales a continuación:',
  'roi.mentalHealthInfo': 'Información de Salud Mental',
  'roi.sudInfo': 'Información sobre Trastorno por Uso de Sustancias (42 CFR Parte 2)',
  'roi.hivInfo': 'Información sobre VIH/SIDA',
  'roi.initialPlaceholder': 'Iniciales',
  'roi.witnessHeading': 'Testigo (Opcional)',
  'roi.witnessNote': 'Puede requerirse un testigo para ciertas autorizaciones.',
  'roi.witnessName': 'Nombre del Testigo',
  'roi.witnessNamePlaceholder': 'Nombre completo del testigo',
  'roi.witnessDate': 'Fecha del Testigo',
  'roi.witnessSignatureNote': 'Nota: La firma del testigo se recogerá en la sección de Firma.',

  // Housing Assessment section
  'housing.title': 'Formulario de Evaluación de Apoyos Comunitarios',
  'housing.subtitle': 'Navegación de Vivienda y Mantenimiento de Vivienda (CalAIM)',
  'housing.referralSource': 'Fuente de Referencia',
  'housing.mcp': 'MCP',
  'housing.ecm': 'ECM',
  'housing.provider': 'Proveedor',
  'housing.self': 'Autoreferido',
  'housing.other': 'Otro',
  'housing.specifyOther': 'Especificar otro',
  'housing.managedCarePlan': 'Plan de Atención Administrada (MCP)',
  'housing.managedCarePlanPlaceholder': 'ej., Partnership HealthPlan',
  'housing.assessmentDate': 'Fecha de Evaluación',
  'housing.required': 'Obligatorio',
  'housing.housingStatus': 'A. Estado de Vivienda (Obligatorio)',
  'housing.currentSituation': 'Situación de Vivienda Actual (marque una):',
  'housing.selectSituation': 'Por favor seleccione la situación de vivienda actual',
  'housing.lengthSituation': 'Duración de la Situación Actual',
  'housing.primaryBarriers': 'Barreras Principales para la Vivienda',
  'housing.checkAllApply': '(marque todas las que apliquen)',
  'housing.eligibilityMedical': 'B. Elegibilidad y Apropiación Médica',
  'housing.healthNeeds': 'Necesidades Médicas, de Salud Conductual o Funcionales que justifican los servicios de CS',
  'housing.howImprove': 'Cómo el apoyo de vivienda mejorará los resultados de salud y reducirá los costos',
  'housing.navigationAssessment': 'C. Evaluación de Navegación de Vivienda',
  'housing.sustaingingAssessment': 'D. Evaluación de Mantenimiento de Vivienda',
  'housing.riskSafety': 'E. Evaluación de Riesgo y Seguridad',
  'housing.memberGoals': 'F. Metas del Miembro',
  'housing.serviceRecommendation': 'G. Recomendación de Servicio',
  'housing.assessorInfo': 'Información del Evaluador',
  'housing.assessorName': 'Nombre y Título del Evaluador',
  'housing.assessorNamePlaceholder': 'Ingrese nombre y título del evaluador',
  'housing.clearSignature': 'Borrar Firma',
  'housing.drawSignature': 'Dibujar Firma',
  'housing.typeSignature': 'Escribir Firma',
  'housing.uploadSignature': 'Subir Firma',
  'housing.typeYourName': 'Escriba su nombre',
  'housing.chooseFile': 'Elegir Archivo',
  'housing.uploadImageFile': 'Por favor suba un archivo de imagen',
  'housing.fileSizeLimit': 'El tamaño del archivo debe ser menor a 5MB',

  // Housing barriers
  'housing.lowIncome': 'Ingresos bajos/sin ingresos',
  'housing.poorCredit': 'Mal crédito / deuda de alquiler',
  'housing.priorEviction': 'Desalojo previo',
  'housing.criminalHistory': 'Historial de justicia criminal',
  'housing.disability': 'Discapacidad / condición crónica',
  'housing.behavioralHealth': 'Necesidades de salud conductual',
  'housing.lackDocuments': 'Falta de documentos (ID, SSN, certificado de nacimiento)',
  'housing.languageBarriers': 'Barreras del idioma',
  'housing.transportation': 'Barreras de transporte',
  'housing.lengthSituationLabel': 'Duración de la Situación Actual',

  // Housing situations
  'housing.unsheltered': 'Sin refugio',
  'housing.unshelteredDesc': 'Calle, campamento, vehículo',
  'housing.emergencyShelter': 'Refugio de emergencia / vivienda transicional',
  'housing.doubledUp': 'Temporalmente duplicado',
  'housing.imminentRisk': 'En riesgo inminente de quedarse sin hogar',
  'housing.imminentRiskDesc': 'Dentro de 30-60 días',
  'housing.evictionRisk': 'Con vivienda pero en riesgo de desalojo',
  'housing.recentlyHoused': 'Recientemente alojado',
  'housing.recentlyHousedDesc': 'Dentro de los últimos 12 meses',

  // Risk factors
  'housing.creditHistory': 'Historial crediticio',
  'housing.criminalBackground': 'Antecedentes penales',
  'housing.poorRentHistory': 'Historial de alquiler deficiente',
  'housing.noIncome': 'Sin ingresos',
  'housing.miscAppointments': 'Citas/servicios perdidos',
  'housing.behavioralSymptoms': 'Síntomas de salud conductual',
  'housing.lackTenancySkills': 'Falta de habilidades de inquilinato',

  // Member support needs
  'housing.supportNeedsLabel': 'El miembro necesita apoyo con (marque todos los que correspondan):',
  'housing.tenantRights': 'Entender derechos/responsabilidades del inquilino',
  'housing.budgeting': 'Presupuesto y planificación de alquiler',
  'housing.utilitySetup': 'Configuración/mantenimiento de servicios públicos',
  'housing.landlordComm': 'Comunicación con el propietario',
  'housing.crisisPrevention': 'Prevención de crisis e intervención temprana',
  'housing.ongoingServices': 'Conexión a servicios/apoyos continuos',

  // Risk & Safety screening
  'housing.safetyConcerns': 'Preocupaciones inmediatas de seguridad en la vivienda actual:',
  'housing.evictionRisk30': 'Riesgo de desalojo dentro de 30 días:',
  'housing.repeatedLoss': 'Historial de pérdida repetida de vivienda (2+ veces en 24 meses):',
  'housing.mitigationPlan': 'Si sí a alguna, describa el plan de mitigación:',
  'housing.mitigationPlanPlaceholder': 'Describa el plan de mitigación...',

  // Member goals
  'housing.memberGoalsLabel': 'Meta(s) de Vivienda Declarada(s) por el Miembro',
  'housing.memberGoalsPlaceholder': '¿Cuáles son las metas de vivienda del miembro?',

  // Service recommendation
  'housing.recommendedSupports': 'Apoyo(s) Comunitario(s) Recomendado(s):',
  'housing.housingNavigation': 'Navegación de Vivienda',
  'housing.housingSustaining': 'Mantenimiento de Vivienda',
  'housing.both': 'Ambos',
  'housing.durationFrequency': 'Duración/Frecuencia Solicitada',
  'housing.durationFrequencyPlaceholder': 'ej., Semanal por 6 meses',
  'housing.rationale': 'Justificación',
  'housing.rationalePlaceholder': 'Explique la justificación para esta recomendación...',

  // Assessor information
  'housing.titlePlaceholder': 'Título del puesto',
  'housing.assessorSignatureDate': 'Fecha de Firma del Evaluador',
  'housing.assessorSignature': 'Firma del Evaluador',
  'housing.assessorSigRequired': 'El nombre del evaluador es requerido',

  // Signature options
  'housing.draw': 'Dibujar',
  'housing.type': 'Escribir',
  'housing.upload': 'Subir',
  'housing.typeFullName': 'Escriba el nombre completo del evaluador',
  'housing.signaturePreview': 'Vista previa de firma',
  'housing.uploadedSignature': 'Firma subida',
  'housing.remove': 'Quitar',
  'housing.signatureCaptured': 'Firma capturada:',

  // Common labels
  'housing.yes': 'Sí',
  'housing.no': 'No',

  // Housing - Eligibility & Medical
  'housing.healthRelatedNeeds': 'Necesidad(es) Relacionada(s) con la Salud que Contribuyen a la Inestabilidad de Vivienda',
  'housing.healthRelatedNeedsPlaceholder': 'Describa las necesidades relacionadas con la salud...',
  'housing.relevantDiagnoses': 'Diagnósticos o Condiciones Relevantes (si aplica)',
  'housing.relevantDiagnosesPlaceholder': 'Liste los diagnósticos relevantes...',
  'housing.howHousingHelps': 'Cómo el apoyo de vivienda mejorará o estabilizará los resultados de salud',
  'housing.howHousingHelpsPlaceholder': 'Describa cómo el apoyo de vivienda ayudará...',
  'housing.lengthPlaceholder': 'ej., 6 meses, 2 años',
  'housing.describeOtherBarriers': 'Describa otras barreras',

  // Housing Navigation
  'housing.navCompleteNote': '(Complete si solicita Navegación de Vivienda)',
  'housing.navAssistanceLabel': 'El Miembro Necesita Asistencia Con (marque todas las que apliquen):',
  'housing.navHousingSearch': 'Búsqueda de vivienda e identificación de unidades',
  'housing.navApplications': 'Completar solicitudes de vivienda',
  'housing.navDocuments': 'Obtener identificación/documentos',
  'housing.navAccommodation': 'Solicitudes de acomodación razonable',
  'housing.navLandlord': 'Compromiso/negociación con el propietario',
  'housing.navLeaseTerms': 'Entender los términos del contrato de arrendamiento',
  'housing.navMoveIn': 'Coordinación de mudanza',
  'housing.navRentalAssistance': 'Conexión con asistencia de alquiler o depósitos',

  // Housing Preferences
  'housing.preferencesHeading': 'Preferencias de Vivienda',
  'housing.locationCity': 'Ubicación/Ciudad',
  'housing.locationCityPlaceholder': 'Ciudad o área preferida',
  'housing.unitType': 'Tipo de Unidad:',
  'housing.unitStudio': 'Estudio',
  'housing.unit1BR': '1 Hab.',
  'housing.unit2BR': '2 Hab.',
  'housing.unitOther': 'Otro',
  'housing.accessibilityNeeds': 'Necesidades de Accesibilidad:',
  'housing.accessibilityNone': 'Ninguna',
  'housing.accessibilityADA': 'ADA',
  'housing.accessibilityOther': 'Otro',
  'housing.incomeSources': 'Fuente(s) de Ingreso:',
  'housing.incomeSsiSsdi': 'SSI/SSDI',
  'housing.incomeEmployment': 'Empleo',
  'housing.incomeGa': 'GA',
  'housing.incomeNone': 'Ninguno',
  'housing.incomeOther': 'Otro',

  // Housing Sustaining
  'housing.sustainCompleteNote': '(Complete si solicita Mantenimiento de Vivienda)',
  'housing.leaseStatus': 'Estado Actual del Contrato de Arrendamiento:',
  'housing.newLease': 'Contrato nuevo',
  'housing.existingLease': 'Contrato existente',
  'housing.informalArrangement': 'Acuerdo informal',
  'housing.riskFactorsLabel': 'Factores de Riesgo para Pérdida de Vivienda (marque todos los que apliquen):',
  'housing.riskRentUtilities': 'Dificultad para pagar alquiler/servicios públicos',
  'housing.riskLeaseViolations': 'Violaciones de contrato o avisos',
  'housing.riskConflict': 'Conflicto con propietario/vecinos',
  'housing.riskMaintainingUnit': 'Dificultad para mantener la unidad',

  // Partnership Consent section
  'pc.title': 'Formulario de Consentimiento de Apoyos Comunitarios',
  'pc.subtitle': 'Partnership HealthPlan of California - Divulgación de Información',
  'pc.introParagraph': 'Los proveedores de atención médica, los pagadores de salud y las agencias de servicios sociales se han unido para proporcionar Servicios de Apoyos Comunitarios (CS) para ayudar a promover su salud y bienestar. Para permitir que Partnership HealthPlan of California ("Partnership") y/o otras entidades compartan su información de atención médica y otra información personal entre sí para ayudar a proporcionarle estos Servicios de CS, primero debe dar su autorización. Al completar este formulario, usted está autorizando el uso y la divulgación de su información de atención médica y otra información personal por parte de las siguientes entidades que participan en CS ("Entidades de CS"): proveedores de atención médica como hospitales, médicos y farmacias; Partnership y otros planes de atención administrada que administran los beneficios de Medi-Cal y pagan los servicios que usted recibe bajo Medi-Cal; organizaciones comunitarias que deben cumplir con las leyes de privacidad de atención médica; proveedores basados en escuelas como enfermeras, trabajadores sociales y consejeros; los Departamentos de California de Servicios de Atención Médica, Salud Pública, Servicios Sociales y Servicios de Desarrollo; y agencias del condado, incluyendo, pero no limitado a, planes de salud mental; y proveedores y administradores de casos en centros correccionales, pero solo para los propósitos establecidos a continuación. Su autorización permitirá a las Entidades de CS usar y divulgar su información de atención médica y otra información personal para los siguientes propósitos ("Propósitos"): permitir que estas entidades aborden sus necesidades sociales relacionadas con la salud, incluyendo servicios de navegación de transición de vivienda; depósitos de vivienda; vivienda a corto plazo post-hospitalización; atención residencial a corto plazo incluyendo vivienda, comidas y monitoreo médico continuo; servicios de cuidador; programas de habilitación diurna; asistencia en la transición de un centro de enfermería a un centro de vida asistida o residencia privada; servicios de apoyo en el hogar; adaptaciones o modificaciones del hogar; comidas médicamente adaptadas; y centros de desintoxicación ("Propósitos"). La información que usted autorice para uso y divulgación puede compartirse en formato electrónico seguro, por escrito o verbalmente para coordinar los Servicios de CS para usted.',
  'pc.authStatement': 'Autorizo y solicito que Partnership HealthPlan of California y las Entidades de CS participantes nombradas en el Anexo A usen y compartan cualquiera de mi información de atención médica u otra información personal entre sí por la razón indicada en esta Autorización.',
  'pc.chooseOne': 'Elija UNA de las siguientes opciones:',
  'pc.initialHere': 'INICIALES AQUÍ',
  'pc.consentCommHeading': 'Consentimiento para comunicación por el Programa CS:',
  'pc.consentCommText': 'Al poner mis iniciales aquí, estoy permitiendo que TODAS las Entidades de CS listadas en el Anexo A usen y compartan mi información de atención médica y otra información personal sobre mi historial médico, condición física y mental, y recepción de servicios sociales, y se comuniquen entre sí para proporcionar Servicios de CS. Los tipos de información de salud y otra información confidencial que estoy autorizando entre las Entidades de CS incluyen:',
  'pc.consentComm1': 'Información de salud protegida (PHI), incluyendo información sobre mi atención médica, historial médico, resultados de pruebas de laboratorio y condiciones y tratamiento actuales o futuros;',
  'pc.consentComm2': 'Información de salud mental, incluyendo diagnósticos y tratamientos actuales y pasados de mis condiciones de salud mental, excluyendo notas de psicoterapia que solo se comparten si firmo un formulario de consentimiento por separado;',
  'pc.consentComm3': 'Información del Programa de Educación Individualizado y otra información sobre servicios sociales proporcionados en la escuela;',
  'pc.consentComm4': 'Información de elegibilidad/inscripción de Medi-Cal, que incluye ingresos y cierta otra información demográfica y geográfica relacionada con mi elegibilidad para Servicios y beneficios;',
  'pc.consentComm5': 'Información de vivienda/personas sin hogar, incluyendo mi estado de vivienda, historial y apoyos; y',
  'pc.consentComm6': 'Información limitada de justicia penal, incluyendo datos de reserva, fechas y ubicación de encarcelamiento, y estado de supervisión. Mi consentimiento no se aplica a mi historial criminal, cargos y estado migratorio.',
  'pc.declineHeading': 'Declino participar en CS:',
  'pc.declineText': 'Entiendo que el programa CS permite a las Entidades de CS estar en contacto entre sí para coordinar mi atención. Declino participar en el programa CS. Puedo solicitar participar en programas de gestión de casos para los cuales soy elegible.',
  'pc.furtherAuthIntro': 'Además, al poner mis iniciales a continuación, autorizo específicamente la divulgación de la siguiente información (esta información NO será divulgada a menos que usted lo permita específicamente)',
  'pc.mentalHealthText': 'Información de salud mental, incluyendo diagnóstico, plan de tratamiento y nombre del proveedor. Esto no incluye notas de psicoterapia que solo se comparten si firmo un formulario de consentimiento por separado.',
  'pc.hivText': 'Resultados de Prueba de VIH (Código de Salud y Seguridad § 120980 (g))',
  'pc.sudHeading': 'Información sobre Trastorno por Uso de Sustancias',
  'pc.sudText': 'Los registros de trastorno por uso de sustancias ("SUD") están protegidos por las reglas federales de confidencialidad (42 CFR Parte 2). Las reglas federales no permiten ninguna divulgación adicional de información que identifique a un paciente como teniendo o habiendo tenido un trastorno por uso de sustancias, ya sea por referencia a información disponible públicamente, o a través de prueba de dicha identificación por otra persona, a menos que una divulgación adicional sea permitida por el consentimiento escrito de la persona cuya información se está dando o según lo permita 42 CFR Parte 2. Las reglas federales restringen cualquier uso de la información para investigar o enjuiciar, con respecto a un delito, a cualquier paciente con un trastorno por uso de sustancias, excepto según lo dispuesto en §§ 2.12(c)(5) y 2.65. Al completar esta sección, usted está autorizando a las Entidades de CS a usar y divulgar la siguiente información de SUD para los Propósitos descritos en este formulario: sus diagnósticos actuales y pasados de uso de drogas o alcohol, medicamentos, tratamiento, pruebas de laboratorio, historial de trauma, altas de centros, y cualquier otra información de SUD sobre usted que provenga de un proveedor de trastorno por uso de sustancias/alcohol sujeto a las regulaciones federales de confidencialidad de SUD (42 C.F.R. Parte 2). Los registros de SUD (o la información contenida en ellos) que se usen o divulguen para tratamiento, pago u operaciones de atención médica por ciertas Entidades de CS, incluyendo proveedores de atención médica, pueden ser redivulgados según lo permitido por las regulaciones federales de HIPAA, excepto para usos y divulgaciones en procedimientos civiles, penales, administrativos y legislativos en su contra. Sus notas de consejería de SUD no serán compartidas a menos que firme un formulario de consentimiento por separado.',
  'pc.sudInitialText': 'Ponga sus iniciales aquí para permitir que las Entidades de CS en el Anexo A usen y compartan su información de SUD como se describe anteriormente, excluyendo las notas de consejería de SUD.',
  'pc.expirationHeading': 'Expiración del Formulario',
  'pc.expirationChooseOne': 'Elija UNA de las siguientes opciones:',
  'pc.expirationStandard': 'Expiración estándar:',
  'pc.expirationStandardText': 'Este formulario expirará 1 año a partir de la fecha de hoy; O',
  'pc.expirationCustom': 'Expiración:',
  'pc.expirationCustomText': 'Este formulario expirará el:',
  'pc.expirationCustomNote': '. Esta fecha no puede ser menos de 6 meses (para participar en los servicios de CS), pero puede ser más de 1 año a partir de la fecha de hoy.',
  'pc.understandHeading': 'Entiendo que:',
  'pc.understand1': 'Puedo revocar esta Autorización en cualquier momento llamando a Partnership al (800) 863-4115 o enviando una solicitud de revocación firmada a:',
  'pc.understand1Address': 'Partnership HealthPlan of California\nAttn: Enhanced Health Services\n4665 Business Center Drive\nFairfield, CA 94534',
  'pc.understand2': 'Una revocación es efectiva cuando se recibe, pero puede no aplicarse a información ya compartida, basada en mi consentimiento previo para usar o divulgar información.',
  'pc.understand3': 'Puedo elegir no firmar este formulario y hacerlo no afectará mi tratamiento o atención, mi elegibilidad para o capacidad de recibir Servicios, o el pago de Servicios. Sin embargo, mi capacidad para participar en los Servicios de CS puede verse afectada al no firmar esta Autorización.',
  'pc.understand4': 'Incluso si no firmo este formulario, bajo las leyes federales y estatales de privacidad, algunas de las Entidades de CS pueden compartir mi información confidencial para tratamiento, pago y otros propósitos, pero los proveedores sujetos a las leyes federales de confidencialidad de uso de sustancias generalmente no pueden compartir mi información de trastorno por uso de sustancias sin mi consentimiento (42 CFR Parte 2).',
  'pc.understand5': 'La información que autorizo para uso o divulgación puede ser re-compartida por las Entidades de CS, pero solo en cumplimiento con esta Autorización y la ley aplicable.',
  'pc.understand6': 'Puedo obtener una copia de la información de salud que se está compartiendo.',
  'pc.understand7': 'Tengo el derecho de pedir una copia de este formulario y una me será enviada.',
  'pc.understand8': 'Puedo obtener una lista de todas las Entidades de CS a las cuales mi información ha sido divulgada, incluyendo aquellas entidades identificadas en el Anexo A, contactando a Partnership.',
  'pc.understand9': 'Si incluyo voluntariamente mi número de teléfono arriba, doy mi consentimiento para recibir mensajes de texto o llamadas para comunicarse conmigo sobre mis opciones de consentimiento y cómo mi información de salud y otra información confidencial puede ser compartida (pueden aplicarse tarifas estándar de mensajes y datos).',
  'pc.understand10': 'Cada uno de los derechos anteriores se extiende a cualquier representante que yo autorice bajo la ley aplicable.',
  'pc.signatureNote': '[firma en la siguiente página]',
  'pc.signatureNoteDetail': 'Por favor proceda al Paso 7 (Firma) para proporcionar su firma digital. La firma se aplicará a este formulario.',
  'pc.signatureMemberHeading': 'Firma del Miembro',
  'pc.signatureInstruction': 'Si está firmando esta Autorización en su propio nombre, complete la primera línea. Si está firmando en nombre de otra persona, complete la segunda línea. Si está firmando en nombre de un menor de 12 a 17 años, el menor debe completar la primera línea y usted debe completar la segunda línea.',
  'pc.signatureCheckNote1': 'El Nombre y la Firma del Beneficiario se capturarán en el Paso 7 (Sección de Firma)',
  'pc.signatureCheckNote2': 'Si firma como Representante, por favor proporcione su información a continuación:',
  'pc.repName': 'Nombre del Representante (si aplica)',
  'pc.repNamePlaceholder': 'Déjelo en blanco si usted es el beneficiario',
  // Signature section  
  'signature.digitalSignature': 'Firma Digital',
  'signature.memberOnly': 'La firma es solo para el miembro o representante autorizado. Si usted es personal preparando este formulario, deje esta sección en blanco.',
  'signature.staffInstruction': 'Instrucción para el personal: no firme aquí mientras completa el formulario. Deje los campos de firma vacíos para el firmante real.',
  'signature.signedBy': 'Firmado Por (Nombre Impreso)',
  'signature.signedByPlaceholder': 'Nombre legal completo',
  'signature.date': 'Fecha',
  'signature.relationshipLabel': 'Relación con el Miembro (si no es el miembro)',
  'signature.relationshipPlaceholder': 'ej., Padre, Tutor Legal, Poder Notarial (dejar en blanco si usted es el miembro)',
  'signature.relationshipNote': 'Solo complete esto si está firmando en nombre del miembro (representante autorizado)',
  'signature.chooseMethod': 'Elija el Método de Firma',
  'signature.drawSignature': 'Dibujar Firma',
  'signature.typeSignature': 'Escribir Firma',
  'signature.uploadSignature': 'Subir Firma',
  'signature.clearSignature': 'Borrar Firma',
  'signature.typeYourName': 'Escriba su nombre',
  'signature.clickToSign': 'Haga clic arriba para firmar',
  'signature.signaturePreview': 'Vista previa de la firma:',
  'signature.chooseFile': 'Elegir Archivo',
  'signature.dragDropFile': 'o arrastre y suelte imagen de firma',
  'signature.uploadImageFile': 'Por favor suba un archivo de imagen',
  'signature.fileSizeLimit': 'El tamaño del archivo debe ser menor a 5MB',
  'signature.witnessHeading': 'Firma del Testigo (si aplica)',
  'signature.witnessNote': 'Complete esta sección solo si se requiere un testigo para la autorización.',
  'signature.witnessChooseMethod': 'Método de Firma del Testigo',
  'signature.witnessDrawSignature': 'Dibujar Firma del Testigo',
  'signature.witnessTypeSignature': 'Escribir Firma del Testigo',
  'signature.witnessUploadSignature': 'Subir Firma del Testigo',
  'signature.witnessClearSignature': 'Borrar Firma del Testigo',
  'signature.witnessTypeYourName': 'Escriba nombre del testigo',
  'signature.witnessSignaturePreview': 'Vista previa de firma del testigo:',
  'signature.witnessChooseFile': 'Elegir Archivo del Testigo',
  'signature.witnessDragDropFile': 'o arrastre y suelte imagen de firma del testigo',
  'signature.clearAndResign': 'Borrar y Firmar de Nuevo',
  'signature.signatureRequired': '⚠️ Firma Requerida - Por favor proporcione su firma arriba para continuar',
  'signature.signatureCaptured': '✓ Firma capturada exitosamente',
  'signature.staffInstructionBlank': 'Si usted es personal preparando este formulario, deje la sección de firma en blanco.',
  'signature.signatureAppearance': 'Así es como aparecerá su firma en los documentos.',
  'signature.legalAcknowledgment': 'Reconocimiento Legal',
  'signature.acknowledgmentText': 'Al firmar arriba, reconozco que:',
  'signature.acknowledgment1': 'He leído y entendido toda la información proporcionada en este formulario',
  'signature.acknowledgment2': 'La información que he proporcionado es verdadera y precisa según mi mejor conocimiento',
  'signature.acknowledgment3': 'Estoy firmando voluntariamente y no he sido coaccionado',
  'signature.acknowledgment4': 'Entiendo que mi firma electrónica tiene el mismo efecto legal que una firma escrita a mano',
  'signature.acknowledgment5': 'Soy el miembro nombrado en este formulario, o estoy autorizado a firmar en su nombre',
  'signature.witnessSignatureTitle': 'Firma del Testigo',
  'signature.witnessSpecified': 'Se especificó un testigo',
  'signature.witnessSignBelow': 'Por favor haga que el testigo firme abajo.',
  'signature.witnessSignatureArea': 'Firma del testigo',
  'signature.imageFileFormat': 'PNG, JPG hasta 5MB',
  'signature.witnessSignatureAlt': 'Firma del testigo',
  'signature.signatureWillAppearOn': 'Su firma aparecerá en:',
  'signature.ecmReleaseOfInformation': 'Liberación de Información ECM',
  'signature.housingAssessment': 'Evaluación de Vivienda',
  'signature.cssConsentForm': 'Formulario de Consentimiento CSS',
  'signature.carePlan': 'Plan de Atención (16 páginas)',
  'signature.consentPartnership': 'Consentimiento de Sociedad',
  'signature.cyecmAssessment': 'Evaluación CYECM',
  'signature.witnessSignatureCaptured': '✓ Firma del testigo capturada exitosamente',

  // PDF Preview section
  'preview.title': 'Vista Previa de PDFs Generados',
  'preview.reviewDocuments': 'Revise todos los {count} documentos antes de enviar',
  'preview.generatedDocuments': 'Documentos Generados',
  'preview.ready': 'Listo',
  'preview.pages': '{count} página{plural}',
  'preview.openNewTab': 'Abrir en Nueva Pestaña',
  'preview.open': 'Abrir',
  'preview.download': 'Descargar',
  'preview.downloading': 'Descargando...',
  'preview.selectPdf': 'Seleccione un PDF para previsualizar',
  'preview.pdfsGenerated': '{count} PDFs generados y listos para enviar',
  'preview.goBackEdit': 'Regresar y Editar',
  'preview.edit': 'Editar',
  'preview.submitSaveAll': 'Enviar y Guardar Todos los PDFs',
  'preview.submit': 'Enviar',
  'preview.saving': 'Guardando...',
  'preview.savingDatabase': 'Guardando en Base de Datos...',
  'preview.back': 'Atrás',
  'preview.failedSave': 'Error al guardar formularios. Por favor intente de nuevo.',};

const DICTIONARIES: Record<Language, TranslationDict> = { en, es };

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  toggleLanguage: () => void;
  t: (key: string, params?: Record<string, string | number>) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const interpolate = (template: string, params?: Record<string, string | number>): string => {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (_, key) => {
    const v = params[key];
    return v === undefined || v === null ? `{${key}}` : String(v);
  });
};

const readStoredLanguage = (): Language => {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === 'en' || stored === 'es') return stored;
  } catch {
    // ignore
  }
  return 'en';
};

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => readStoredLanguage());

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, language);
    } catch {
      // ignore
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = language;
    }
  }, [language]);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguageState((prev) => (prev === 'en' ? 'es' : 'en'));
  }, []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>): string => {
      const dict = DICTIONARIES[language];
      const fallback = DICTIONARIES.en;
      const template = dict[key] ?? fallback[key] ?? key;
      return interpolate(template, params);
    },
    [language]
  );

  const value = useMemo<LanguageContextValue>(
    () => ({ language, setLanguage, toggleLanguage, t }),
    [language, setLanguage, toggleLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = (): LanguageContextValue => {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used inside a <LanguageProvider>');
  }
  return ctx;
};
